'use client';
/**
 * (main)/ClientShell — s203 Nuclear isolation.
 *
 *  배경: production 에서 React #310 (Hook order) + #300 (Invalid element) 발생.
 *  (main)/layout.tsx 가 12+ client component 를 SSR 트리에 직접 마운트하는 동안
 *  Next.js 15 + React 19 + dynamic import 조합에서 SSR/hydrate hook 트리 mismatch
 *  edge case 발생.
 *
 *  Fix: layout 을 서버 component 로 단순화하고, hook-heavy + 공통 chrome 을
 *  본 ClientShell 로 이동. AuthProvider / ToastProvider / Navigation /
 *  Tracker 류 등 SSR 에 의미 없는 컴포넌트 모두 { ssr: false } 로
 *  격리 → SSR HTML 에는 children (페이지 본문) 만 직렬화 → SEO 영향 없음, hydrate
 *  단계에서 chrome 채워짐.
 *
 *  주의: { ssr: false } 컴포넌트는 SSR 시 placeholder 만 렌더되어 BAILOUT 신호를
 *  만들지만, blog/[slug] notFound() 는 페이지 자체에서 throw 하는 경로 (s187 후속
 *  fix) 이므로 본 변경 영향 없음.
 */

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import ErrorBoundary from '@/components/ErrorBoundary';
import TopLoadingBar from '@/components/TopLoadingBar';
// s214 C3: ToastProvider/AuthProvider 가 ssr:false 면 children 트리 전체가 BAILOUT
// → 모든 (main) 페이지의 본문이 SSR 안되고 RSC payload 로만 전송 (h1/h2 0개, SEO 무의미).
// 두 provider 는 useEffect 안에서만 브라우저 API 사용 → SSR-safe. 직접 import 로 전환.
import { ToastProvider } from '@/components/Toast';
import { AuthProvider } from '@/components/AuthProvider';
// s209 fix(track-regression): listener-only 트래커 3종은 dynamic({ssr:false}) 격리 해제.
// 이 셋은 useEffect 안에서 document-level click listener / fetch beacon 만 attach 하고
// 항상 null 을 렌더한다. 즉 SSR HTML 은 변하지 않으면서, 메인 청크에 묶여 hydrate 즉시
// 리스너가 붙는다. s203 nuclear 적용 후 모바일/저속망에서 chunk 도착 전 클릭이 navigate
// 되어 conversion_events.cta_click 이 누락된 게 4/28 회귀의 직접 원인 — 6 source → 1.
import CtaGlobalTracker from '@/components/CtaGlobalTracker';
import PageViewTracker from '@/components/PageViewTracker';
import BehaviorTracker from '@/components/BehaviorTracker';
// MASTER v3 §2(2026-09-26): 전 페이지 상단 고정 「부정공 TALK」 배너(StickyTalkBanner) 제거 —
// → E-0(2026-09-26, FINAL_DESIGN_E §1): 상단 밴드만 44px 로 «복원»(TopBand). 순수 마크업이라
//   SSR 로 모든 방문자에게 같은 HTML 이 나간다. 가입 유도·게이트 제거분은 되살리지 않았다.
import type { BandCopy } from '@/lib/banner/band-copy-shared';
import type { HeaderRegionData } from '@/lib/v3/header-region-match';
import { NavDataProvider } from '@/components/nav/nav-data-context';
// ⑭ R14a — 탐색 v2(서버 렌더 · 탭 5). 킬 스위치 app_config ui/nav_v2=false 면 아래 옛 Navigation(ssr:false)이 그대로 돈다.
import NavSwitch from '@/components/nav/NavSwitch';
import type { AdReserve } from '@/lib/banner/ad-reserve';
import SiteFooter from '@/components/nav/SiteFooter';
import OpenchatTag from '@/components/openchat/OpenchatTag';
import ShareFab from '@/components/share/ShareFab';
import type { CompanyInfo } from '@/lib/company-info-shared';

// C 레인 CLS(2026-09-26): ssr:false 라 SSR HTML 에 헤더 자리가 없었다 → 청크가 오면 45px 헤더가 끼어들며
//   본문 전체(body > div flex 컨테이너)를 밀었다(운영 Lighthouse CLS 최대 기여). `loading` 은 ssr:false 여도
//   SSR 로 렌더되므로 헤더와 «같은 토큰 높이» 의 자리를 미리 잡는다. 헤더 높이를 바꿀 곳은 여전히 토큰 한 곳이다.
const Navigation             = dynamic(() => import('@/components/Navigation').then(m => m.Navigation),     { ssr: false, loading: () => <div aria-hidden="true" style={{ height: 'var(--kd-header-h)', borderBottom: '1px solid transparent', boxSizing: 'border-box' }} /> });
const PWAInstallTracker      = dynamic(() => import('@/components/PWAInstallTracker'),                       { ssr: false });
const VitalsReporter         = dynamic(() => import('@/components/web-vitals/VitalsReporter'),               { ssr: false });
// signup-fix-1031 — 로그인 회원 필수 동의 게이트(비로그인 0 렌더) · 가입/로그인 GA 이벤트 1회 발화.
const ConsentGate            = dynamic(() => import('@/components/consent/ConsentGate'),                    { ssr: false });
const AuthEventBeacon        = dynamic(() => import('@/components/consent/AuthEventBeacon'),                { ssr: false });
// MASTER v3 §2(2026-09-26): StickySignupBar(하단 고정 가입 바) · KakaoChannelAddModal
// (마운트 시 자동 노출되는 카카오 채널 추가 모달) · MarketingConsentModalMount(마케팅
// 동의 + 카카오 채널 추가 자동 팝업) — 세 자동 유도 컴포넌트를 여기서 걷어냈다.
// 로그인 「기능」 자체(AuthProvider/Kakao OAuth)는 건드리지 않았다 — 관리자 로그인 경로 보존.
//
// Node 판정(2026-09-26) — 「페이지당 홍보 요소 1개」를 회원 리텐션 넛지에도 적용하며
// GlobalMissionBar(첫 미션·출석 바)와 거주지 유도 모달(ResidenceNudgeModal)을 렌더 경로에서
// 뺐고, CL(2026-10-15)에서 나머지 5개(ProfileCompleteBanner·InstallBanner·WelcomeReward·
// WelcomeToast·SmartPushPrompt)도 렌더하지 않게 됐다. 2026-10-25 미사용 정리(cleanup-dead)에서
// 7개 파일을 모두 제거했다(출석 기능 퇴역 포함) — 되살릴 때는 git 이력에서 꺼낸다.

interface Props {
  children: React.ReactNode;
  serverLoggedIn: boolean;
  /** W4-1 — 헤더 지역 칩 데이터(layout 이 regions 표에서 만든다). 비면 칩을 숨긴다. */
  headerRegion?: HeaderRegionData;
  /** ⑭ 통합 — 전역 검색 시트의 인기 검색(v_search_top_keywords_clean, 서버 캐시). */
  navPopular?: string[];
  /** ⑤ 밴드 문안(app_config kakao/band — layout 이 서버에서 읽는다). */
  bandCopy?: BandCopy;
  /** ⑤ 공지 전광판 34px SSR 예약(노출될 공지가 있을 때 — 레인 C CLS ①). */
  noticeReserved?: boolean;
  /** ⑭ 킬 스위치(app_config ui/nav_v2 · 기본 true). false → 옛 Navigation 그대로. */
  navV2?: boolean;
  /** L6 — 탭 라벨 한 벌(app_config nav/labels_v2). 서버에서 읽어 내려준다. */
  navLabelsV2?: boolean;
  /** M6 — 하단 탭 재선정(app_config nav/tabs_v3 · 기본 끔). 서버에서 읽어 내려준다. */
  navTabsV3?: boolean;
  /** search_unify1 — ui.popular_region(서버 값 · 기본 off). */
  popularOn?: boolean;
  /** ⑭(R14c 이관) AdBanner 자리 예약 판정(layout 이 재고 표에서 서버로 읽는다 · 방문자 무관). */
  adReserve?: AdReserve;
  /** RD-3 D1 — 슬롯 안 서버 렌더 하우스 소재(HouseAdFallback · 클라이언트 경계 밖에서 만들어 내려온다). */
  adHouse?: React.ReactNode;
  /** ADS-F — 사업자 정보(app_config 'company' · layout 이 서버에서 읽는다 · 방문자 무관). 비면 푸터에 아무것도 안 보인다. */
  company?: CompanyInfo | null;
  /** oc-tag — 부정공 정보공유방 가장자리 태그(app_config ui/openchat_tag · 기본 켬). */
  openchatOn?: boolean;
}

export default function ClientShell({ children, serverLoggedIn, headerRegion, navV2 = true, navLabelsV2, navTabsV3, navPopular, popularOn = false, company = null, openchatOn = false }: Props) {
  // H3-4 — 홈 첫 화면에서 감출 것들의 판정.
  // ⚠️ 컴포넌트를 지우지 않는다. 렌더만 끊는다 — 다른 화면에서는 그대로 쓴다.
  const pathname = usePathname() ?? '';
  // ⚠️ C 레인(2026-09-26): 서버 렌더에서 홈의 usePathname() 은 «/index» 다 — `=== '/'` 로 가르면 홈이
  //    React #418 로 통째 다시 그려진다. 판정은 lib/route/is-home.ts 한 곳.
  // CL(2026-10-15 §1-6 · §4-4) — 렌더하지 않는 크롬: 상단 카톡·카페 밴드(TopBand) · 공지 마퀴(NoticeBanner) · 상단 광고 띠(AdBanner)
  //   · 프로필 완성/설치/환영/환영 토스트/푸시 프롬프트 · SiteDock · TalkFab · 출석·미션 배너. H1 위 배너 0.
  //   ⚠️ 컴포넌트 파일은 지우지 않는다(개명·이동 금지) — 마운트만 끊었다. 되살리려면 이 파일에 dynamic import + 마운트.
  //   bandCopy·noticeReserved·adReserve·adHouse 는 호환용 prop 으로만 남는다(렌더 0).

  return (
    <ToastProvider>
      <NavDataProvider headerRegion={headerRegion} popular={navPopular} navV2={navV2} popularOn={popularOn}>
      <AuthProvider serverLoggedIn={serverLoggedIn}>
        <TopLoadingBar />
        <VitalsReporter />
        <NavSwitch v2={navV2} labelsV2={navLabelsV2} tabsV3={navTabsV3} pathname={pathname} legacy={<Navigation headerRegion={headerRegion} />} />
        {/* v5-V1: 좌측 Sidebar 제거. 링크 14개가 하단 탭 4개 + /more 와 통째로 중복이었고
             데스크탑(≥1024px)에서만 보이는데 /apt 데스크탑 비중은 30% 다
             (30일 실측 데스크탑 351 / 모바일 829). 상단 헤더 네비가 주 동선이고
             나머지는 /more 가 받는다. */}
        <div style={{ display: 'flex', maxWidth: 'var(--container-grid)', margin: '0 auto', gap: 24, padding: '0 clamp(12px, 3vw, 24px)', alignItems: 'flex-start' }}>
          <main id="main-content" style={{
            flex: 1, minWidth: 0,
            paddingTop: 'clamp(12px,3vw,20px)',
            paddingBottom: 72,
            minHeight: 'calc(100vh - 48px)',
            backgroundColor: 'var(--bg-base)',
            color: 'var(--text-primary)',
            /* ⛔⛔ U-1b 실측(2026-08-30) — 여기가 `hidden` 이면 main 이 «스크롤 컨테이너» 가 되어
                 그 안의 `position: sticky` 가 «전부 죽는다». 실측으로 잡은 사망자 셋:
                   · 상세 점프바(top:45)  — 스크롤 2,400px 에서 top 이 -1,920 이었다(화면 밖)
                   · 상세 우측 레일        — --rail-top: 103px 을 «아무도 안 쓰고 있었다»
                   · 지역 셀렉 하단 적용 바(bottom:0)
                 U-1a 에서 레일 정지선을 118→103 으로 «고친» 것도 사실은 죽은 값을 고친 것이었다.
                 → `clip` 은 가로를 «똑같이 잘라내면서» 스크롤 컨테이너를 만들지 «않는다».
                    그래서 가로 넘침 방어는 그대로 두고 sticky 만 되살린다.
                 ⚠️ Safari 16 미만은 `clip` 을 모른다 — 그 경우 `visible` 로 떨어져
                    «자르지 않는다». 넘침이 있으면 가로 스크롤이 생기지만, ds-6x6 이
                    6폭 × 3모드 × 6페이지에서 넘침 0 을 확인하고 있어 방어 대상이 지금은 없다. */
            overflowX: 'clip',
          }}>
            <ErrorBoundary>
              {children}
            </ErrorBoundary>
          </main>
        </div>
        <PWAInstallTracker />
        <ConsentGate />
        <AuthEventBeacon />

        <PageViewTracker />
        <BehaviorTracker />
        <CtaGlobalTracker />
        {/* oc-tag(2026-11-01 · Node 시안 C) — 오른쪽 가장자리 「정보방」 태그 → 부정공 정보공유방. 콘텐츠 면만 · 스크롤 30%/8초 뒤 · 누르면 30일 숨김. */}
        {openchatOn && <OpenchatTag />}
        {/* Q4(2026-11-05) — 정보방 태그 바로 위 「공유」 태그. 노출 조건은 정보방 태그와 같다(openchatOn + openchatTagAllowed). */}
        {openchatOn && <ShareFab />}
        {/* CL §1-6 — 푸터 = 정적 허브 링크(시도 · 도구 · 분석 글 · 회사). 내부 링크 손실 방지. 카톡·카페·위성 링크 없음. */}
        <SiteFooter tops={headerRegion?.tops ?? []} company={company} />
      </AuthProvider>
      </NavDataProvider>
    </ToastProvider>
  );
}

// v4-C4-1: SignupPopupModalMount 제거.
// 30일 실측 — popup_signup_modal 551노출 / dismiss 488 (88.6% 가 닫힘).
// 그 압박의 결과가 30일 신규 가입 7명이다. 그리고 이제 1순위 전환 목표는
// 고객등록 폼(리드폼)이라 가입 유도가 폼과 경쟁해서는 안 된다.

